import { useState, useCallback, useRef, useEffect } from 'react';
import jsonlint from 'jsonlint';
import JxButton from '../../../JxControls/JxButtons/JxButton';
import { jxScrollbarThin } from '../../../JxControls/jxScrollbarStyle';
import umlCanvasEngine from '../../Core/UmlCanvasEngine';
import './UmlSchemaBodyTab.css';

const DEBOUNCE_MS = 300;
const SYNC_MESSAGE_MS = 500;

// Zelfde validatie-aanpak als DtoEditorDialog.jsx (pages/DashboardComponents/DevTab) —
// jsonlint i.p.v. los JSON.parse (betere foutmeldingen), gedebouncet zodat niet elke
// toetsaanslag een lint-pass triggert. Zie Jerny Source - DevTab (rk=31d4d50c4f0e4a9fad7ccdd8e176e7e5).
function validateJson(raw) {
    if (!raw.trim()) return { valid: false, error: null, parsed: null };
    try {
        const parsed = jsonlint.parse(raw);
        return { valid: true, error: null, parsed };
    } catch (e) {
        return { valid: false, error: e.message, parsed: null };
    }
}

function currentSchemaJson() {
    return JSON.stringify(umlCanvasEngine.toSaveSchema(), null, 2);
}

/**
 * UmlSchemaBodyTab — editable JSON view of what would go into note.body on Save.
 *
 * @param {{ onSave: () => void, isSaving: boolean, name: string }} props
 */
export default function UmlSchemaBodyTab({ onSave, isSaving, name }) {
    const [raw, setRaw] = useState(() => currentSchemaJson());
    const [validation, setValidation] = useState(() => validateJson(raw));
    const [syncMessage, setSyncMessage] = useState(null);
    const debounceRef = useRef(null);
    const syncTimeoutRef = useRef(null);

    useEffect(() => {
        // umlCanvasEngine alone is sufficient — it cascades umlConnectorEngine's
        // notify() into its own (see UmlCanvasEngine constructor, 2026-07-14),
        // so this catches every schema-content mutation (shapes, connectors,
        // viewConfig) regardless of which engine originated it.
        function refresh() {
            const fresh = currentSchemaJson();
            setRaw(fresh);
            setValidation(validateJson(fresh));
            setSyncMessage('JSON has been updated due to canvas change');
            clearTimeout(syncTimeoutRef.current);
            syncTimeoutRef.current = setTimeout(() => setSyncMessage(null), SYNC_MESSAGE_MS);
        }
        const unsubscribe = umlCanvasEngine.subscribe(refresh);
        return () => {
            unsubscribe();
            clearTimeout(syncTimeoutRef.current);
        };
    }, []);

    const handleChange = useCallback((event) => {
        const val = event.target.value;
        setRaw(val);
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => setValidation(validateJson(val)), DEBOUNCE_MS);
    }, []);

    const handleApply = useCallback(() => {
        if (!validation.valid) return;
        umlCanvasEngine.loadFromSchema(validation.parsed);
    }, [validation]);

    const handleCopyToClipboard = useCallback(() => {
        navigator.clipboard.writeText(raw).then(() => {
            setSyncMessage('Copied to clipboard');
            clearTimeout(syncTimeoutRef.current);
            syncTimeoutRef.current = setTimeout(() => setSyncMessage(null), SYNC_MESSAGE_MS);
        });
    }, [raw]);

    const isEmpty = !raw.trim();
    const statusClass = syncMessage
        ? 'usbt-status--sync'
        : isEmpty
          ? 'usbt-status--empty'
          : validation.valid
            ? 'usbt-status--valid'
            : 'usbt-status--invalid';
    const statusIcon = syncMessage ? '↻' : isEmpty ? '○' : validation.valid ? '✓' : '✕';
    const statusMsg = syncMessage ?? (isEmpty ? 'Type JSON in' : validation.valid ? 'valid JSON' : validation.error);

    return (
        <div className="usd-body usbt-body">
            <div className="usd-import-text">Editable body preview - modify at your own risk.</div>

            <textarea
                className={`usd-textarea usbt-textarea ${jxScrollbarThin}`}
                value={raw}
                onChange={handleChange}
                spellCheck={false}
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
            />

            <div className="usd-button-row usbt-button-row">
                <JxButton className="jx-button-sm" label="Save" onClick={onSave} disabled={!name?.trim() || isSaving} />
                <div className="usbt-button-spacer" />
                <JxButton className="jx-button-sm" label="Copy to Clipboard" onClick={handleCopyToClipboard} disabled={isEmpty} />
                <JxButton className="jx-button-sm" label="Apply to Canvas" onClick={handleApply} disabled={!validation.valid} />
            </div>

            <div className={`usbt-status-bar ${statusClass}`}>
                <span className="usbt-status-icon">{statusIcon}</span>
                <span className="usbt-status-msg">{statusMsg}</span>
            </div>
        </div>
    );
}
